import React, { useCallback, useContext } from 'react';
import { Form, Formik } from 'formik';

import Routes from 'routing/Routes';
import FormWarningsContext from 'contexts/FormWarningsContext';
import { Col, Row } from 'components/bootstrap';
import { FormSubmit } from 'components/common';
import FormWarningsProvider from 'contexts/FormWarningsProvider';
import type { SearchFilter } from 'views/types';
import history from 'util/History';
import validateSearchFilterForm from 'search-filter/validateForm';
import TitleFormGroup from 'search-filter/components/form-elements/TitleFormGroup';
import DescriptionFormGroup from 'search-filter/components/form-elements/DescriptionFormGroup';
import useUserDateTime from 'hooks/useUserDateTime';

import QueryStringFormGroup from '../form-elements/QueryStringFromGroup';

export type FormValues = Pick<SearchFilter, 'queryString' | 'title' | 'description'>

type Props = {
  children?: React.ReactNode,
  disableSubmitButton?: boolean,
  initialValues?: Partial<FormValues>,
  submitButtonText?: string,
  submitLoadingText?: string,
  onSubmit: (searchFilter: FormValues) => Promise<void>
}

const UserSearchFilterForm = ({
  children,
  initialValues,
  submitButtonText,
  submitLoadingText,
  onSubmit,
  disableSubmitButton,
}: Props) => {
  const { setFieldWarning } = useContext(FormWarningsContext);
  const { userTimezone } = useUserDateTime();

  const handleCancel = useCallback(() => history.push(Routes.pluginRoute('MY-FILTERS')), []);
  const validate = useCallback((values: FormValues) => validateSearchFilterForm(values, { setFieldWarning, userTimezone }), [userTimezone, setFieldWarning]);

  return (
    <Formik onSubmit={onSubmit}
            validateOnMount
            validate={validate}
            initialValues={initialValues}>
      {({ isValid, isSubmitting, values }) => {
        const disableSubmit = disableSubmitButton || !isValid || !values.queryString;

        return (
          <Form className="form form-horizontal">
            <QueryStringFormGroup />
            <TitleFormGroup />
            <DescriptionFormGroup />
            {children}
            <Row>
              <Col md={9} mdOffset={3}>
                <FormSubmit submitButtonText={submitButtonText}
                            disabledSubmit={disableSubmit}
                            submitLoadingText={submitLoadingText}
                            isSubmitting={isSubmitting}
                            isAsyncSubmit
                            onCancel={handleCancel} />
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
};

const UserSearchFilterFormWrapper = (props: Props) => (
  <FormWarningsProvider>
    <UserSearchFilterForm {...props} />
  </FormWarningsProvider>
);

const defaultProps = {
  submitButtonText: 'Save',
  submitLoadingText: 'Saving...',
  initialValues: {},
  disableSubmitButton: false,
  children: undefined,
};

UserSearchFilterForm.defaultProps = defaultProps;
UserSearchFilterFormWrapper.defaultProps = defaultProps;

export default UserSearchFilterFormWrapper;
